import { useEffect, useId, useMemo, useState } from "react"

import { useMetricLabelsTransport } from "@/metrics/labels"
import { supportsAgentJudgeTools } from "@/metrics/runtime"
import { useMetricsListRecordDataTransport } from "@/metrics/transport-data"
import {
  Button,
  Checkbox,
  GroupLabel,
  Input,
  MultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"
import { Trash2 } from "lucide-react"

import type { MetricRecordData } from "@/types/metrics/contracts"
import type { MetricMetadata } from "@/types/metrics/metadata"

import { QuestionTextarea } from "../../configuration/components/QuestionTextarea"
import {
  FIELD_CONTAINER_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
} from "../../configuration/styles/index"

const AGENT_JUDGE_MODELS = [
  { id: "openai:gpt-4.1-mini-2025-04-14", label: "GPT-4.1 Mini" },
  { id: "openai:gpt-4.1-2025-04-14", label: "GPT-4.1" },
] as const

const AGENT_JUDGE_TOOLS = [
  {
    id: "get_transcript",
    group: "Conversation",
    label: "Read the conversation",
  },
  {
    id: "search_transcript",
    group: "Conversation",
    label: "Search the conversation",
  },
  {
    id: "search_transcript_regex",
    group: "Conversation",
    label: "Advanced pattern search",
  },
  {
    id: "get_evidence_manifest",
    group: "Simulation & execution",
    label: "Check what evidence is available",
  },
  {
    id: "get_simulation_outcome",
    group: "Simulation & execution",
    label: "Read the simulation result",
  },
  {
    id: "query_simulation_frames",
    group: "Simulation & execution",
    label: "Explore events and structured data",
  },
  {
    id: "get_trace_spans",
    group: "Simulation & execution",
    label: "Inspect execution traces",
  },
  {
    id: "get_run_context",
    group: "Test context",
    label: "Read run and test-case details",
  },
  {
    id: "get_dependency_metric_outputs",
    group: "Other metrics",
    label: "Read results from selected metrics",
  },
] as const

const DEPENDENCY_TOOL_ID = "get_dependency_metric_outputs"
const MAX_METRIC_DEPENDENCIES = 10
const LEGACY_AGENT_JUDGE_DEFAULT_TOOL_IDS: AgentJudgeToolId[] = [
  "get_transcript",
  "search_transcript",
  "search_transcript_regex",
  "get_trace_spans",
  "query_simulation_frames",
  "get_run_context",
]

type AgentJudgeToolId = (typeof AGENT_JUDGE_TOOLS)[number]["id"]

interface AgentJudgeMetadataEditorProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
  outputConfiguration?: React.ReactNode
  textAreaRef?: React.RefObject<HTMLTextAreaElement>
  questionPlaceholder: string
}

interface AgentJudgeReadOnlyMetadataProps {
  children: React.ReactNode
  metricRecordData: MetricRecordData
  outputConfiguration?: React.ReactNode
}

export function AgentJudgeReadOnlyMetadata({
  children,
  metricRecordData,
  outputConfiguration,
}: AgentJudgeReadOnlyMetadataProps) {
  const metadata = metricRecordData.metric_metadata ?? {}
  const modelVersion = metadata.model_version ?? AGENT_JUDGE_MODELS[0].id
  const model = AGENT_JUDGE_MODELS.find(({ id }) => id === modelVersion)
  const enabledTools = metadata.enabled_tools?.map(
    (id) => AGENT_JUDGE_TOOLS.find((tool) => tool.id === id)?.label ?? id,
  )

  return (
    <div className="space-y-4">
      {children}
      {outputConfiguration}
      <div>
        <p className="text-sm font-medium text-coval-text">Model</p>
        <p className="text-sm text-coval-text">
          {model?.label ?? `Unsupported saved model: ${modelVersion}`}
        </p>
      </div>
      {enabledTools && (
        <div>
          <p className="text-sm font-medium text-coval-text">Read-only tools</p>
          <p className="text-sm text-coval-text">
            {enabledTools.length > 0 ? enabledTools.join(", ") : "None"}
          </p>
        </div>
      )}
    </div>
  )
}

export function AgentJudgeMetadataEditor({
  metricRecordData,
  onChange,
  outputConfiguration,
  textAreaRef,
  questionPlaceholder,
}: AgentJudgeMetadataEditorProps) {
  const modelId = useId()
  const metadata = useMemo(
    () => metricRecordData.metric_metadata ?? {},
    [metricRecordData.metric_metadata],
  )
  const modelVersion =
    typeof metadata.model_version === "string" &&
    metadata.model_version.length > 0
      ? metadata.model_version
      : AGENT_JUDGE_MODELS[0].id
  const isSupportedModel = AGENT_JUDGE_MODELS.some(
    (model) => model.id === modelVersion,
  )
  const updateMetadata = (updates: Partial<MetricMetadata>) => {
    const nextMetadata = { ...metadata, ...updates }
    if (
      (metricRecordData.dependency_metric_ids?.length ?? 0) > 0 &&
      !nextMetadata.enabled_tools?.includes(DEPENDENCY_TOOL_ID)
    ) {
      nextMetadata.enabled_tools = [
        ...(nextMetadata.enabled_tools ?? LEGACY_AGENT_JUDGE_DEFAULT_TOOL_IDS),
        DEPENDENCY_TOOL_ID,
      ]
    }
    onChange({
      ...metricRecordData,
      metric_metadata: nextMetadata,
    })
  }
  useEffect(() => {
    if (
      (metricRecordData.dependency_metric_ids?.length ?? 0) > 0 &&
      !metadata.enabled_tools?.includes(DEPENDENCY_TOOL_ID)
    ) {
      onChange({
        ...metricRecordData,
        metric_metadata: {
          ...metadata,
          enabled_tools: [
            ...(metadata.enabled_tools ?? LEGACY_AGENT_JUDGE_DEFAULT_TOOL_IDS),
            DEPENDENCY_TOOL_ID,
          ],
        },
      })
    }
  }, [metricRecordData, metadata, onChange])
  const enabledTools = Array.isArray(metadata.enabled_tools)
    ? metadata.enabled_tools
    : LEGACY_AGENT_JUDGE_DEFAULT_TOOL_IDS
  const showDependencies =
    enabledTools.includes(DEPENDENCY_TOOL_ID) ||
    (metricRecordData.dependency_metric_ids?.length ?? 0) > 0

  return (
    <div className="space-y-6">
      <QuestionTextarea
        metricRecordData={metricRecordData}
        onChange={onChange}
        label="Agent Judge question"
        required
        placeholder={questionPlaceholder}
        textAreaRef={textAreaRef}
      />
      {outputConfiguration}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div className={FIELD_CONTAINER_STYLES}>
          <label
            htmlFor={modelId}
            id={`${modelId}-label`}
            className={LABEL_STYLES}
          >
            Model
          </label>
          <Select
            value={modelVersion}
            onValueChange={(model_version) => updateMetadata({ model_version })}
          >
            <SelectTrigger id={modelId} aria-labelledby={`${modelId}-label`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AGENT_JUDGE_MODELS.map((model) => (
                <SelectItem key={model.id} value={model.id}>
                  {model.label}
                </SelectItem>
              ))}
              {!isSupportedModel && (
                <SelectItem value={modelVersion}>
                  Unsupported saved model: {modelVersion}
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          <p className="mt-1.5 text-xs text-coval-text-muted">
            Agent Judge supports text evaluations only.
          </p>
        </div>
        {supportsAgentJudgeTools(metricRecordData) && (
          <AgentJudgeToolControls
            dependencyMetricIds={metricRecordData.dependency_metric_ids ?? []}
            metadata={metadata}
            updateMetadata={updateMetadata}
          />
        )}
      </div>
      {showDependencies && (
        <AgentJudgeDependenciesPicker
          metricRecordData={metricRecordData}
          onChange={onChange}
        />
      )}
    </div>
  )
}

interface AgentJudgeToolControlsProps {
  dependencyMetricIds: string[]
  metadata: MetricMetadata
  updateMetadata: (updates: Partial<MetricMetadata>) => void
}

function AgentJudgeToolControls({
  dependencyMetricIds,
  metadata,
  updateMetadata,
}: AgentJudgeToolControlsProps) {
  const availableTools = AGENT_JUDGE_TOOLS
  const enabledTools = Array.isArray(metadata.enabled_tools)
    ? metadata.enabled_tools
    : LEGACY_AGENT_JUDGE_DEFAULT_TOOL_IDS
  const configuredTools = enabledTools.filter(
    (tool): tool is AgentJudgeToolId =>
      availableTools.some(({ id }) => id === tool),
  )
  const displayedTools =
    dependencyMetricIds.length > 0 &&
    !configuredTools.includes(DEPENDENCY_TOOL_ID)
      ? [...configuredTools, DEPENDENCY_TOOL_ID]
      : configuredTools

  return (
    <div className="rounded-md border border-coval-border bg-surface-default p-3">
      <p className="text-xs font-medium text-coval-text">
        Evidence the judge can use
      </p>
      <p className="mt-1 text-xs leading-relaxed text-coval-text-muted">
        Choose what information this judge can inspect while evaluating a
        conversation.
      </p>
      <div className="mt-3 space-y-3">
        {[...new Set(availableTools.map(({ group }) => group))].map((group) => (
          <div key={group}>
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-coval-text-muted">
              {group}
            </p>
            <div className="space-y-1.5">
              {availableTools
                .filter((tool) => tool.group === group)
                .map((tool) => (
                  <label
                    key={tool.id}
                    className="flex items-center gap-2 text-sm text-coval-text"
                  >
                    <Checkbox
                      checked={displayedTools.includes(tool.id)}
                      disabled={
                        tool.id === DEPENDENCY_TOOL_ID &&
                        dependencyMetricIds.length > 0
                      }
                      onCheckedChange={(checked) => {
                        const enabled_tools = checked
                          ? enabledTools.includes(tool.id)
                            ? enabledTools
                            : [...enabledTools, tool.id]
                          : enabledTools.filter((id) => id !== tool.id)
                        updateMetadata({ enabled_tools })
                      }}
                    />
                    {tool.label}
                  </label>
                ))}
            </div>
          </div>
        ))}
      </div>
      {dependencyMetricIds.length > 0 && (
        <p className="mt-3 text-xs text-coval-text-muted">
          Reading dependency outputs stays enabled while dependencies are
          selected.
        </p>
      )}
    </div>
  )
}

interface AgentJudgeDependenciesPickerProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

export function AgentJudgeDependenciesPicker({
  metricRecordData,
  onChange,
}: AgentJudgeDependenciesPickerProps) {
  const dependenciesLabelId = useId()
  const selectedIds = metricRecordData.dependency_metric_ids ?? []
  const metricsQuery = useMetricsListRecordDataTransport()
  const metricLabelsQuery = useMetricLabelsTransport(selectedIds)
  const metrics = Object.values(metricsQuery.data ?? {}).filter(
    (metric) => metric.id !== metricRecordData.id,
  )
  const knownIds = new Set(metrics.map((metric) => metric.id))
  const unavailableIds = selectedIds.filter((id) => !knownIds.has(id))
  const atLimit = selectedIds.length >= MAX_METRIC_DEPENDENCIES
  const options = [
    ...metrics.map((metric) => ({
      value: metric.id,
      label: metric.metric_display_name,
      disabled: atLimit && !selectedIds.includes(metric.id),
      tags: [
        metric.metric_name,
        metric.output_type ?? "unknown output",
        metric.locked ? "built-in" : "custom",
      ],
      trailingContent: (
        <span className="text-xs text-coval-text-muted">
          {metric.output_type ?? "Unknown output"} ·{" "}
          {metric.locked ? "Built-in" : "Custom"}
        </span>
      ),
    })),
    ...unavailableIds.map((id) => ({
      value: id,
      label:
        metricLabelsQuery.data?.[id]?.metric_display_name ??
        `Unavailable metric (${id})`,
      disabled: false,
      tags: ["unavailable"],
      trailingContent: (
        <span className="text-xs text-coval-text-muted">
          {metricLabelsQuery.data?.[id]?.output_type ?? "Unknown output"} ·
          Unavailable
        </span>
      ),
    })),
  ]

  const updateDependencies = (dependencyMetricIds: string[]) => {
    const normalizedIds = Array.from(new Set(dependencyMetricIds))
    if (
      normalizedIds.length > MAX_METRIC_DEPENDENCIES &&
      normalizedIds.some((id) => !selectedIds.includes(id))
    ) {
      return
    }
    const metadata = metricRecordData.metric_metadata ?? {}
    const enabledTools = Array.isArray(metadata.enabled_tools)
      ? metadata.enabled_tools
      : LEGACY_AGENT_JUDGE_DEFAULT_TOOL_IDS
    const nextTools =
      normalizedIds.length > 0 && !enabledTools.includes(DEPENDENCY_TOOL_ID)
        ? [...enabledTools, DEPENDENCY_TOOL_ID]
        : enabledTools

    onChange({
      ...metricRecordData,
      dependency_metric_ids: normalizedIds,
      metric_metadata: { ...metadata, enabled_tools: nextTools },
    })
  }

  return (
    <div className={FIELD_CONTAINER_STYLES}>
      <div className={LABEL_ROW_STYLES}>
        <GroupLabel id={dependenciesLabelId} className={LABEL_STYLES}>
          Metric dependencies
        </GroupLabel>
        <span className="text-xs text-coval-text-muted">
          {selectedIds.length}/{MAX_METRIC_DEPENDENCIES}
        </span>
      </div>
      <p className="mb-2 text-xs text-coval-text-muted">
        Allow this judge to inspect outputs from metrics selected in the same
        run. Dependencies are never added to a run automatically.
      </p>
      <MultiSelect
        aria-labelledby={dependenciesLabelId}
        value={selectedIds}
        onValueChange={updateDependencies}
        options={options}
        searchable
        searchPlaceholder="Search metrics"
        placeholder={
          metricsQuery.isLoading ? "Loading metrics…" : "Select metrics"
        }
        disabled={metricsQuery.isLoading}
        error={metricsQuery.isError}
      />
      {metricsQuery.isError && (
        <div className="mt-2 flex items-center gap-2 text-xs text-coval-danger-text">
          <span>Metrics could not be loaded.</span>
          <Button
            type="button"
            variant="ghost"
            onClick={() => void metricsQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      )}
      {!metricsQuery.isLoading &&
        !metricsQuery.isError &&
        metrics.length === 0 && (
          <p className="mt-2 text-xs text-coval-text-muted">
            No other active metrics are available.
          </p>
        )}
      {unavailableIds.length > 0 && (
        <p className="mt-2 text-xs text-coval-warning-text">
          {unavailableIds.length} saved{" "}
          {unavailableIds.length === 1 ? "dependency is" : "dependencies are"}{" "}
          unavailable. Remove {unavailableIds.length === 1 ? "it" : "them"} to
          stop preserving{" "}
          {unavailableIds.length === 1 ? "that reference" : "those references"}.
        </p>
      )}
    </div>
  )
}

interface AgentJudgeOutputConfigurationProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

export function AgentJudgeCategoricalConfiguration({
  metricRecordData,
  onChange,
}: AgentJudgeOutputConfigurationProps) {
  const [newCategory, setNewCategory] = useState("")
  const categories = metricRecordData.metric_metadata?.categories ?? []
  const trimmedNewCategory = newCategory.trim()
  const categoryHasComma = trimmedNewCategory.includes(",")

  const updateCategories = (nextCategories: string[]) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        categories: nextCategories,
      },
    })
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        <div className="flex flex-1 flex-col gap-1">
          <label className={LABEL_STYLES} htmlFor="agent-judge-category">
            Add Category
          </label>
          <Input
            id="agent-judge-category"
            value={newCategory}
            placeholder="Enter a category"
            onChange={(event) => setNewCategory(event.target.value)}
          />
        </div>
        <Button
          disabled={
            !trimmedNewCategory ||
            categoryHasComma ||
            categories.some(
              (category) => category.trim() === trimmedNewCategory,
            )
          }
          onClick={() => {
            updateCategories([...categories, trimmedNewCategory])
            setNewCategory("")
          }}
          className="self-end"
        >
          Add
        </Button>
      </div>
      {categoryHasComma && (
        <span className="text-sm text-coval-danger-text">
          Category labels cannot contain commas.
        </span>
      )}
      <div className="flex flex-wrap gap-2">
        {categories.map((category) => (
          <div
            key={category}
            className="flex items-center gap-1 rounded-md bg-surface-1 px-2 py-1"
          >
            <span>{category}</span>
            <Button
              icon
              variant="ghost"
              onClick={() =>
                updateCategories(
                  categories.filter((value) => value !== category),
                )
              }
              className="text-coval-text-muted hover:text-coval-danger-text"
              tooltip={`Remove ${category}`}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </div>
      {categories.length < 2 && (
        <span className="text-sm text-coval-danger-text">
          Add at least two categories.
        </span>
      )}
    </div>
  )
}

export function AgentJudgeNumericalConfiguration({
  metricRecordData,
  onChange,
}: AgentJudgeOutputConfigurationProps) {
  const baseId = useId()
  const minValueId = `${baseId}-min-value`
  const maxValueId = `${baseId}-max-value`
  const updateValue = (
    key: "min_value" | "max_value",
    value: number | undefined,
  ) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        [key]: value,
      },
    })
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label htmlFor={minValueId} className={LABEL_STYLES}>
            Min Value
          </label>
        </div>
        <Input
          id={minValueId}
          type="number"
          value={metricRecordData.metric_metadata?.min_value ?? ""}
          onChange={(event) =>
            updateValue(
              "min_value",
              event.target.value === ""
                ? undefined
                : Number(event.target.value),
            )
          }
          placeholder="Enter minimum value"
        />
      </div>
      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label htmlFor={maxValueId} className={LABEL_STYLES}>
            Max Value
          </label>
        </div>
        <Input
          id={maxValueId}
          type="number"
          value={metricRecordData.metric_metadata?.max_value ?? ""}
          onChange={(event) =>
            updateValue(
              "max_value",
              event.target.value === ""
                ? undefined
                : Number(event.target.value),
            )
          }
          placeholder="Enter maximum value"
        />
      </div>
    </div>
  )
}
